import React, { useState } from 'react'
import { Eye, EyeOff, Copy, Check } from 'lucide-react'
import clsx from 'clsx'

/**
 * SecretField - Input field for sensitive data like API keys
 *
 * Features:
 * - Masked display by default (shows last 4 characters)
 * - Toggle visibility
 * - Copy to clipboard
 * - Works for both display and edit modes
 */
const SecretField = ({
  value,
  onChange,
  placeholder = 'Enter API key...',
  readOnly = false,
  className = '',
  label,
  id,
  error,
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [copied, setCopied] = useState(false)

  // Mask the value, showing only last 4 characters
  const getMaskedValue = (val) => {
    if (!val) return ''
    if (val.length <= 8) return '••••••••'
    return '••••••••••••••••' + val.slice(-4)
  }

  const handleCopy = async () => {
    if (value) {
      try {
        await navigator.clipboard.writeText(value)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } catch (err) {
        console.error('Failed to copy:', err)
      }
    }
  }

  const displayValue = isVisible ? value : getMaskedValue(value)

  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <input
          type={isVisible ? 'text' : 'password'}
          id={id}
          value={readOnly ? displayValue : value}
          onChange={onChange}
          placeholder={placeholder}
          readOnly={readOnly}
          className={clsx(
            'block w-full pr-20 rounded-md shadow-sm text-sm',
            'border-gray-300 dark:border-gray-600',
            'bg-white dark:bg-gray-700',
            'text-gray-900 dark:text-gray-100',
            'placeholder-gray-400 dark:placeholder-gray-500',
            'focus:ring-primary-500 focus:border-primary-500',
            'dark:focus:ring-primary-400 dark:focus:border-primary-400',
            readOnly && 'bg-gray-50 dark:bg-gray-800 cursor-default',
            error && 'border-red-500 focus:ring-red-500 focus:border-red-500'
          )}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 space-x-1">
          {/* Copy button */}
          <button
            type="button"
            onClick={handleCopy}
            className={clsx(
              'p-1 rounded text-gray-400 hover:text-gray-600',
              'dark:hover:text-gray-300 transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-primary-500'
            )}
            title={copied ? 'Copied!' : 'Copy to clipboard'}
          >
            {copied ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </button>

          {/* Toggle visibility button */}
          <button
            type="button"
            onClick={() => setIsVisible(!isVisible)}
            className={clsx(
              'p-1 rounded text-gray-400 hover:text-gray-600',
              'dark:hover:text-gray-300 transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-primary-500'
            )}
            title={isVisible ? 'Hide' : 'Show'}
          >
            {isVisible ? (
              <EyeOff className="w-4 h-4" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  )
}

export default SecretField
